import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../services/api';
import DcaSettingsFields from '../components/masterchef/DcaSettingsFields';
import { CHAIN_LABELS, buildDexScreenerUrl, formatTime, getNativeSymbol, resolveEmaBuyAmount, DEFAULT_EMA_SIZE_BY_CHAIN, formatNum } from '../utils/masterChefUtils';

const CHAINS = ['solana', 'base', 'ethereum', 'bsc'];
const TIMEFRAMES = ['15m', '1h', '4h', '1d'];
const DEFAULT_EMA_TIERS = [
  { multiple: 2, sellPercent: 30 },
  { multiple: 3, sellPercent: 25 },
  { multiple: 5, sellPercent: 25 },
];

function normalizeEmaExit(emaExit = {}) {
  const tiers = Array.isArray(emaExit.tiers) && emaExit.tiers.length > 0
    ? emaExit.tiers.map((t) => ({
      multiple: Number(t.multiple) || 2,
      sellPercent: Number(t.sellPercent) || 25,
    }))
    : DEFAULT_EMA_TIERS;
  return { ...emaExit, tiers };
}

function formatTierSummary(tiers) {
  return (tiers || DEFAULT_EMA_TIERS)
    .map((t) => `${t.multiple}x→${t.sellPercent}%`)
    .join(' · ');
}
const inputClass = 'w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs';

const Toggle = ({ value, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${value ? 'bg-green-600' : 'bg-gray-300'}`}
  >
    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${value ? 'translate-x-5' : 'translate-x-1'}`} />
  </button>
);

function CollapsiblePanel({ title, subtitle, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
      >
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
          {subtitle && <p className="text-[10px] text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700">{children}</div>}
    </div>
  );
}

const STATUS_COLORS = {
  entered: 'text-green-600 dark:text-green-400',
  in_position: 'text-blue-600 dark:text-blue-400',
  bullish: 'text-green-500',
  bearish: 'text-red-500',
  watching: 'text-gray-500',
  error: 'text-red-600',
  idle: 'text-gray-400',
};

function isResolvableAddress(chain, address) {
  const a = String(address || '').trim();
  if (!a) return false;
  if (chain === 'solana') return a.length >= 32;
  return /^0x[a-fA-F0-9]{40}$/.test(a);
}

export default function MasterChefEma() {
  const { dashboard, refreshDashboard } = useOutletContext() || {};
  const [watchlist, setWatchlist] = useState([]);
  const [emaEntry, setEmaEntry] = useState({});
  const [emaExit, setEmaExit] = useState({});
  const [emaRisk, setEmaRisk] = useState({});
  const [scanner, setScanner] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    tokenAddress: '',
    tokenSymbol: '',
    tokenName: '',
    chain: 'solana',
    timeframe: '4h',
    fastPeriod: 9,
    slowPeriod: 21,
    enabled: true,
    executionMode: '',
    positionSizeNative: '',
  });
  const [resolvingToken, setResolvingToken] = useState(false);
  const [resolveError, setResolveError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/masterchef/ema/watchlist');
      setWatchlist(res.data.watchlist || []);
      setEmaEntry(res.data.emaEntry || {});
      setEmaExit(normalizeEmaExit(res.data.emaExit));
      setEmaRisk(res.data.emaRisk || {});
      setScanner(res.data.scanner || {});
    } catch (err) {
      console.error('Failed to fetch EMA watchlist:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (!showAdd || !isResolvableAddress(form.chain, form.tokenAddress)) {
      setResolveError('');
      return undefined;
    }

    const timer = setTimeout(async () => {
      setResolvingToken(true);
      setResolveError('');
      try {
        const res = await api.get('/masterchef/ema/resolve', {
          params: { chain: form.chain, address: form.tokenAddress.trim() },
        });
        setForm((prev) => ({
          ...prev,
          tokenSymbol: res.data.tokenSymbol || '',
          tokenName: res.data.tokenName || '',
        }));
      } catch (err) {
        setResolveError(err.response?.data?.error || 'Could not resolve token');
        setForm((prev) => ({ ...prev, tokenSymbol: '', tokenName: '' }));
      } finally {
        setResolvingToken(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [showAdd, form.chain, form.tokenAddress]);

  const saveSettings = async (patch) => {
    setSaving(true);
    try {
      await api.put('/masterchef/ema/settings', patch);
      await fetchData();
      refreshDashboard?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addToken = async (e) => {
    e.preventDefault();
    if (!form.tokenAddress.trim()) return;
    setSaving(true);
    try {
      await api.post('/masterchef/ema/watchlist', {
        ...form,
        positionSizeNative: form.positionSizeNative === '' ? null : parseFloat(form.positionSizeNative),
      });
      setForm({
        tokenAddress: '',
        tokenSymbol: '',
        tokenName: '',
        chain: 'solana',
        timeframe: emaEntry.defaultTimeframe || '4h',
        fastPeriod: emaEntry.defaultFastPeriod || 9,
        slowPeriod: emaEntry.defaultSlowPeriod || 21,
        enabled: true,
        executionMode: '',
        positionSizeNative: '',
      });
      setResolveError('');
      setShowAdd(false);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add token');
    } finally {
      setSaving(false);
    }
  };

  const updateItem = async (id, patch) => {
    try {
      await api.put(`/masterchef/ema/watchlist/${id}`, patch);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update');
    }
  };

  const removeItem = async (id, symbol) => {
    if (!window.confirm(`Remove ${symbol} from EMA watchlist?`)) return;
    try {
      await api.delete(`/masterchef/ema/watchlist/${id}`);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove');
    }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await api.post('/masterchef/ema/scan');
      setWatchlist(res.data.watchlist || []);
      alert(`Scan complete — ${res.data.result?.scanned || 0} tokens, ${res.data.result?.entered || 0} entries`);
    } catch (err) {
      alert(err.response?.data?.error || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const saveChainDefaultSize = (chain, value) => {
    const positionSizeByChain = {
      ...(emaEntry.positionSizeByChain || {}),
      [chain]: parseFloat(value) || DEFAULT_EMA_SIZE_BY_CHAIN[chain],
    };
    saveSettings({ emaEntry: { positionSizeByChain } });
  };

  const tiers = normalizeEmaExit(emaExit).tiers;

  const updateEmaTier = (index, key, value) => {
    const next = tiers.map((t, i) => (i === index ? { ...t, [key]: value } : t));
    saveSettings({ emaExit: { tiers: next } });
  };

  const addEmaTier = () => {
    saveSettings({ emaExit: { tiers: [...tiers, { multiple: 5, sellPercent: 25 }] } });
  };

  const removeEmaTier = (index) => {
    if (tiers.length <= 1) return;
    saveSettings({ emaExit: { tiers: tiers.filter((_, i) => i !== index) } });
  };

  const d = dashboard || {};
  const proc = d.processes?.emaScanner || {};
  const enabledCount = watchlist.filter((w) => w.enabled !== false).length;

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading EMA watchlist...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">EMA Watchlist</h2>
          <p className="text-xs text-gray-500">
            Curated tokens · bullish EMA cross entry · bearish cross or tier exits
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={runScan}
            disabled={scanning}
            className="px-3 py-1.5 text-xs rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50"
          >
            {scanning ? 'Scanning...' : 'Scan now'}
          </button>
          <button
            type="button"
            onClick={() => setShowAdd(!showAdd)}
            className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            {showAdd ? 'Cancel' : '+ Add token'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Scanner</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              proc.status === 'running'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}>
              {proc.status === 'running' ? 'Running' : 'Stopped'}
            </span>
          </div>
          <dl className="text-xs space-y-1.5 text-gray-600 dark:text-gray-400">
            <div className="flex justify-between">
              <dt>Interval</dt>
              <dd className="text-gray-900 dark:text-white">{proc.intervalSeconds || emaEntry.scanIntervalSeconds || 180}s</dd>
            </div>
            <div className="flex justify-between">
              <dt>Enabled tokens</dt>
              <dd className="text-gray-900 dark:text-white">{enabledCount}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Active positions</dt>
              <dd className="text-gray-900 dark:text-white">{proc.activeEmaPositions || 0}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Last scan</dt>
              <dd className="text-gray-900 dark:text-white">{scanner.lastScanAt ? formatTime(scanner.lastScanAt) : '—'}</dd>
            </div>
          </dl>
        </div>

        <div className="lg:col-span-2">
          <CollapsiblePanel title="Global defaults" subtitle="Scan interval, EMA periods, mode, buy sizes">
            <div className="pt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <label className="flex flex-col gap-1">
              <span className="text-gray-500">Enabled</span>
              <select
                value={emaEntry.enabled !== false ? '1' : '0'}
                onChange={(e) => saveSettings({ emaEntry: { enabled: e.target.value === '1' } })}
                disabled={saving}
                className="px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              >
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-500">Scan interval (s)</span>
              <input
                type="number"
                min="60"
                step="30"
                defaultValue={emaEntry.scanIntervalSeconds || 180}
                onBlur={(e) => saveSettings({ emaEntry: { scanIntervalSeconds: parseInt(e.target.value, 10) || 180 } })}
                className="px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-500">Default TF</span>
              <select
                value={emaEntry.defaultTimeframe || '4h'}
                onChange={(e) => saveSettings({ emaEntry: { defaultTimeframe: e.target.value } })}
                className="px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              >
                {TIMEFRAMES.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-500">Mode</span>
              <select
                value={emaEntry.executionMode || 'dry_run'}
                onChange={(e) => saveSettings({ emaEntry: { executionMode: e.target.value } })}
                className="px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              >
                <option value="dry_run">Dry run</option>
                <option value="live">Live</option>
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-500">Fast EMA</span>
              <input
                type="number"
                min="2"
                defaultValue={emaEntry.defaultFastPeriod || 9}
                onBlur={(e) => saveSettings({ emaEntry: { defaultFastPeriod: parseInt(e.target.value, 10) || 9 } })}
                className="px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-500">Slow EMA</span>
              <input
                type="number"
                min="3"
                defaultValue={emaEntry.defaultSlowPeriod || 21}
                onBlur={(e) => saveSettings({ emaEntry: { defaultSlowPeriod: parseInt(e.target.value, 10) || 21 } })}
                className="px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              />
            </label>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-[10px] font-medium text-gray-500 uppercase mb-2">Default buy size (when token has no override)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {CHAINS.map((chain) => (
                <label key={chain} className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500 w-8">{getNativeSymbol(chain)}</span>
                  <input
                    type="number"
                    step="0.001"
                    min="0.001"
                    defaultValue={emaEntry.positionSizeByChain?.[chain] ?? DEFAULT_EMA_SIZE_BY_CHAIN[chain]}
                    onBlur={(e) => saveChainDefaultSize(chain, e.target.value)}
                    className="flex-1 px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                  />
                </label>
              ))}
            </div>
          </div>
          <p className="text-[10px] text-gray-400 mt-2">
            Master Chef must be running. Restart bot after changing scan interval.
          </p>
          </CollapsiblePanel>
        </div>
      </div>

      <CollapsiblePanel
        title="Strategy rules"
        subtitle={`Entry limits · TP: ${formatTierSummary(tiers)} · stop loss & trailing stop`}
      >
        <div className="pt-3 grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Entry &amp; re-entry</h4>
          <p className="text-[10px] text-gray-400 mb-3">When to open / re-open after a closed trade</p>
          <div className="space-y-2 text-xs">
            <label className="flex flex-col gap-1">
              <span className="text-gray-500">Re-entry cooldown (min)</span>
              <span className="text-[10px] text-gray-400">Wait after bearish cross exit before same token can enter again</span>
              <input
                type="number"
                min="0"
                step="5"
                defaultValue={emaEntry.reentryCooldownMinutes ?? 60}
                onBlur={(e) => saveSettings({ emaEntry: { reentryCooldownMinutes: parseInt(e.target.value, 10) || 0 } })}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-500">Max concurrent EMA trades</span>
              <input
                type="number"
                min="1"
                defaultValue={emaEntry.maxConcurrentEmaPositions ?? 5}
                onBlur={(e) => saveSettings({ emaEntry: { maxConcurrentEmaPositions: parseInt(e.target.value, 10) || 1 } })}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-500">Max daily EMA trades</span>
              <input
                type="number"
                min="1"
                defaultValue={emaEntry.maxDailyEmaTrades ?? 10}
                onBlur={(e) => saveSettings({ emaEntry: { maxDailyEmaTrades: parseInt(e.target.value, 10) || 1 } })}
                className={inputClass}
              />
            </label>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <label className="flex flex-col gap-1">
                <span className="text-gray-500">Min liquidity ($)</span>
                <input
                  type="number"
                  min="0"
                  defaultValue={emaEntry.minLiquidity ?? 5000}
                  onBlur={(e) => saveSettings({ emaEntry: { minLiquidity: parseInt(e.target.value, 10) || 0 } })}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-gray-500">Min MC ($)</span>
                <input
                  type="number"
                  min="0"
                  defaultValue={emaEntry.minMarketCap ?? 10000}
                  onBlur={(e) => saveSettings({ emaEntry: { minMarketCap: parseInt(e.target.value, 10) || 0 } })}
                  className={inputClass}
                />
              </label>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">DCA splits</span>
                <Toggle
                  value={emaEntry.dcaEnabled === true}
                  onChange={(val) => saveSettings({ emaEntry: { dcaEnabled: val } })}
                />
              </div>
              <p className="text-[10px] text-gray-400 mb-2">
                Off by default. EMA entry fires on bullish cross — optional drop-DCA adds on retest instead of chasing.
                Bearish cross exit may close the position before later splits fill.
              </p>
              {emaEntry.dcaEnabled && (
                <DcaSettingsFields
                  values={emaEntry}
                  inputClass={inputClass}
                  onUpdate={(key, val) => saveSettings({ emaEntry: { [key]: val } })}
                />
              )}
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Take profit &amp; exits</h4>
          <p className="text-[10px] text-gray-400 mb-3">Tier sells, moon bag, trailing stop, bearish EMA exit</p>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500">TP tiers (multiple × sell %)</span>
            <button type="button" onClick={addEmaTier} className="text-xs text-cyan-600 hover:text-cyan-800 font-medium">+ Add</button>
          </div>
          <div className="space-y-1 mb-3">
            {tiers.map((tier, i) => (
              <div key={i} className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800/50 px-2 py-1 rounded">
                <span className="text-xs text-gray-400 w-6">T{i + 1}</span>
                <input
                  type="number"
                  step="0.5"
                  min="1"
                  value={tier.multiple}
                  onChange={(e) => updateEmaTier(i, 'multiple', parseFloat(e.target.value) || 2)}
                  className={`${inputClass} w-16`}
                />
                <span className="text-xs text-gray-400">x</span>
                <input
                  type="number"
                  step="5"
                  min="1"
                  max="100"
                  value={tier.sellPercent}
                  onChange={(e) => updateEmaTier(i, 'sellPercent', parseInt(e.target.value, 10) || 25)}
                  className={`${inputClass} w-14`}
                />
                <span className="text-xs text-gray-400">%</span>
                <button type="button" onClick={() => removeEmaTier(i)} className="text-red-400 hover:text-red-600 text-xs ml-auto">×</button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <label className="flex flex-col gap-1">
              <span className="text-gray-500">Moon bag (%)</span>
              <input
                type="number"
                min="0"
                max="50"
                defaultValue={emaExit.moonBagPercent ?? 20}
                onBlur={(e) => saveSettings({ emaExit: { moonBagPercent: parseInt(e.target.value, 10) || 0 } })}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-500">Bearish EMA exit</span>
              <div className="flex items-center gap-2 h-[30px]">
                <Toggle
                  value={emaExit.exitOnBearishCross !== false}
                  onChange={(val) => saveSettings({ emaExit: { exitOnBearishCross: val } })}
                />
                <span className="text-gray-400">{emaExit.exitOnBearishCross !== false ? 'Full sell on cross' : 'Off'}</span>
              </div>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-500">Trailing stop</span>
              <div className="flex items-center gap-2 h-[30px]">
                <Toggle
                  value={emaExit.trailingStopEnabled !== false}
                  onChange={(val) => saveSettings({ emaExit: { trailingStopEnabled: val } })}
                />
                <span className="text-gray-400">{emaExit.trailingStopEnabled !== false ? 'On' : 'Off'}</span>
              </div>
            </label>
            {emaExit.trailingStopEnabled !== false && (
              <>
                <label className="flex flex-col gap-1">
                  <span className="text-gray-500">Trail drop (%)</span>
                  <input
                    type="number"
                    min="5"
                    max="90"
                    defaultValue={emaExit.trailingStopPercent ?? 30}
                    onBlur={(e) => saveSettings({ emaExit: { trailingStopPercent: parseInt(e.target.value, 10) || 30 } })}
                    className={inputClass}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-gray-500">Trail after (x)</span>
                  <input
                    type="number"
                    min="1"
                    step="0.5"
                    defaultValue={emaExit.trailingStopActivationMultiple ?? 10}
                    onBlur={(e) => saveSettings({ emaExit: { trailingStopActivationMultiple: parseFloat(e.target.value) || 10 } })}
                    className={inputClass}
                  />
                </label>
              </>
            )}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Stop loss &amp; risk</h4>
          <p className="text-[10px] text-gray-400 mb-3">Hard stop and cooldown after a losing exit</p>
          <div className="space-y-2 text-xs">
            <label className="flex flex-col gap-1">
              <span className="text-gray-500">Stop loss (% from entry)</span>
              <input
                type="number"
                min="5"
                max="90"
                defaultValue={emaRisk.stopLossPercent ?? 40}
                onBlur={(e) => saveSettings({ emaRisk: { stopLossPercent: parseInt(e.target.value, 10) || 40 } })}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-500">Cooldown after stop loss (min)</span>
              <span className="text-[10px] text-gray-400">Same token blocked from re-entry after SL hit</span>
              <input
                type="number"
                min="0"
                step="5"
                defaultValue={emaRisk.cooldownAfterLossMinutes ?? 30}
                onBlur={(e) => saveSettings({ emaRisk: { cooldownAfterLossMinutes: parseInt(e.target.value, 10) || 0 } })}
                className={inputClass}
              />
            </label>
          </div>
          <p className="text-[10px] text-gray-400 mt-4 leading-relaxed">
            Trades stay open until bearish cross, TP tier, stop loss, or trailing stop — no fixed time limit.
            Re-entry only fires on a fresh bullish EMA cross after cooldown.
          </p>
        </div>
        </div>
      </CollapsiblePanel>

      {showAdd && (
        <form onSubmit={addToken} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-gray-500">Chain *</span>
              <select
                value={form.chain}
                onChange={(e) => setForm({
                  ...form,
                  chain: e.target.value,
                  tokenSymbol: '',
                  tokenName: '',
                })}
                className="px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              >
                {CHAINS.map((c) => <option key={c} value={c}>{CHAIN_LABELS[c] || c}</option>)}
              </select>
            </label>
            <label className="sm:col-span-2 lg:col-span-2 flex flex-col gap-1 text-xs">
              <span className="text-gray-500">Token address *</span>
              <input
                required
                value={form.tokenAddress}
                onChange={(e) => setForm({
                  ...form,
                  tokenAddress: e.target.value,
                  tokenSymbol: '',
                  tokenName: '',
                })}
                placeholder={form.chain === 'solana' ? 'Mint address' : '0x… contract address'}
                className="px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 font-mono text-xs"
              />
            </label>
            <div className="flex flex-col gap-1 text-xs justify-end">
              {resolvingToken && (
                <span className="text-cyan-600 dark:text-cyan-400">Looking up token…</span>
              )}
              {!resolvingToken && form.tokenSymbol && (
                <span className="text-green-600 dark:text-green-400">✓ {form.tokenSymbol}</span>
              )}
              {resolveError && (
                <span className="text-red-500">{resolveError}</span>
              )}
            </div>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-gray-500">Ticker</span>
              <input
                readOnly
                value={form.tokenSymbol}
                placeholder="Auto-detect"
                className="px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300"
              />
            </label>
            <label className="sm:col-span-2 flex flex-col gap-1 text-xs">
              <span className="text-gray-500">Token name</span>
              <input
                readOnly
                value={form.tokenName}
                placeholder="Auto-detect"
                className="px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300"
              />
            </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-gray-500">Timeframe</span>
            <select
              value={form.timeframe}
              onChange={(e) => setForm({ ...form, timeframe: e.target.value })}
              className="px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            >
              {TIMEFRAMES.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-gray-500">Fast / Slow</span>
            <div className="flex gap-1">
              <input
                type="number"
                min="2"
                value={form.fastPeriod}
                onChange={(e) => setForm({ ...form, fastPeriod: parseInt(e.target.value, 10) || 9 })}
                className="w-1/2 px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              />
              <input
                type="number"
                min="3"
                value={form.slowPeriod}
                onChange={(e) => setForm({ ...form, slowPeriod: parseInt(e.target.value, 10) || 21 })}
                className="w-1/2 px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              />
            </div>
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-gray-500">Buy amount ({getNativeSymbol(form.chain)})</span>
            <input
              type="number"
              step="0.001"
              min="0.001"
              value={form.positionSizeNative}
              onChange={(e) => setForm({ ...form, positionSizeNative: e.target.value })}
              placeholder={`Default ${resolveEmaBuyAmount({ chain: form.chain }, emaEntry)}`}
              className="px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-gray-500">Mode override</span>
            <select
              value={form.executionMode}
              onChange={(e) => setForm({ ...form, executionMode: e.target.value })}
              className="px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            >
              <option value="">Use global</option>
              <option value="dry_run">Dry run</option>
              <option value="live">Live</option>
            </select>
          </label>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={saving || resolvingToken || !form.tokenSymbol}
              className="w-full px-3 py-1.5 text-xs rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Adding...' : 'Add to watchlist'}
            </button>
          </div>
          </div>
        </form>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {watchlist.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">
            No tokens on watchlist. Add tokens manually to control API usage.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] text-gray-500 dark:text-gray-400 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <th className="text-left px-4 py-2">Token</th>
                  <th className="text-left px-2 py-2">Chain</th>
                  <th className="text-left px-2 py-2">TF</th>
                  <th className="text-left px-2 py-2">EMA</th>
                  <th className="text-left px-2 py-2">Buy</th>
                  <th className="text-left px-2 py-2">Trend</th>
                  <th className="text-left px-2 py-2">Status</th>
                  <th className="text-right px-2 py-2">PnL</th>
                  <th className="text-left px-2 py-2">Checked</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {watchlist.map((item) => (
                  <tr key={item.id} className="border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-2">
                      <a
                        href={buildDexScreenerUrl(item.chain, item.tokenAddress)}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-gray-900 dark:text-white hover:text-blue-500"
                      >
                        {item.tokenSymbol || 'TOKEN'}
                      </a>
                      <p className="text-[10px] text-gray-400 font-mono truncate max-w-[140px]">{item.tokenAddress}</p>
                    </td>
                    <td className="px-2 py-2 text-xs">{CHAIN_LABELS[item.chain] || item.chain}</td>
                    <td className="px-2 py-2">
                      <select
                        value={item.timeframe || '4h'}
                        onChange={(e) => updateItem(item.id, { timeframe: e.target.value })}
                        className="text-xs px-1 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                      >
                        {TIMEFRAMES.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-2 text-xs text-gray-500">
                      {item.fastPeriod || 9}/{item.slowPeriod || 21}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          step="0.001"
                          min="0.001"
                          key={`${item.id}-${item.positionSizeNative ?? 'default'}`}
                          defaultValue={item.positionSizeNative ?? ''}
                          placeholder={String(resolveEmaBuyAmount(item, emaEntry))}
                          onBlur={(e) => {
                            const raw = e.target.value.trim();
                            const next = raw === '' ? null : parseFloat(raw);
                            const current = item.positionSizeNative ?? null;
                            if (next === current || (next != null && current != null && next === Number(current))) return;
                            updateItem(item.id, { positionSizeNative: next });
                          }}
                          className="w-16 text-xs px-1 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                        />
                        <span className="text-[10px] text-gray-400">{getNativeSymbol(item.chain)}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-xs capitalize">{item.lastTrend || '—'}</td>
                    <td className={`px-2 py-2 text-xs capitalize ${STATUS_COLORS[item.status] || ''}`}>
                      {item.status || 'idle'}
                      {item.skipReason && <span className="text-gray-400 ml-1">({item.skipReason})</span>}
                    </td>
                    <td className="px-2 py-2 text-right text-xs">
                      {item.pnl ? (
                        <div className="leading-tight">
                          <div className={item.pnl.totalPnl >= 0 ? 'text-green-600 dark:text-green-400 font-medium' : 'text-red-500 font-medium'}>
                            {item.pnl.totalPnl >= 0 ? '+' : ''}{formatNum(item.pnl.totalPnl, 3)} {getNativeSymbol(item.chain)}
                          </div>
                          <div className="text-[10px] text-gray-400">
                            {item.pnl.pnlPercent != null && (
                              <span className={item.pnl.totalPnl >= 0 ? 'text-green-500' : 'text-red-400'}>
                                {item.pnl.pnlPercent >= 0 ? '+' : ''}{item.pnl.pnlPercent.toFixed(1)}%
                              </span>
                            )}
                            <span className="ml-1">
                              {item.pnl.openTrades > 0 ? `${item.pnl.openTrades} open` : ''}
                              {item.pnl.openTrades > 0 && item.pnl.closedTrades > 0 ? ' · ' : ''}
                              {item.pnl.closedTrades > 0 ? `${item.pnl.closedTrades} closed` : ''}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-xs text-gray-500">{item.lastCheckedAt ? formatTime(item.lastCheckedAt) : '—'}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => updateItem(item.id, { enabled: item.enabled === false })}
                        className="text-xs text-blue-500 hover:underline mr-2"
                      >
                        {item.enabled === false ? 'Enable' : 'Disable'}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeItem(item.id, item.tokenSymbol)}
                        className="text-xs text-red-500 hover:underline"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
